import { all, call, put, takeEvery, fork, select } from 'redux-saga/effects'

// action
import { productActions } from '@redux/store/index'

// api
import { productApi, ResType } from '@api'

// lib
import { Logger } from '@utils'

import { ProductUpdateType, ProductType, Product, ProductAddType } from '@schemas'
import type { PayloadAction } from '@reduxjs/toolkit'
import { ProductStateType } from '@redux/types'

// API call to add a product
const apiAddProduct = async (body: ProductAddType) => {
	const res: ResType | undefined = await productApi.addProduct({
		body: {
			name: body.name,
			code: body.code,
			usd: body.usd,
			krw: body.krw,
			discountUsd: body.discountUsd,
			discountKrw: body.discountKrw,
			quantity: body.quantity,
			index: body.index,
			useYn: body.useYn,
			alwaysAvailableYn: body.alwaysAvailableYn,
		},
	})
	return res
}

// Fetch updated product list
const apiGetProducts = async (): Promise<ResType | undefined> => {
	const res: ResType | undefined = await productApi.getProducts()
	return res
}

// API call to delete a product
const apiDeleteProduct = async (id: string) => {
	const res: ResType | undefined = await productApi.deleteProduct({
		params: { product_id: id },
	})
	return res
}

// API call to update a product
const apiUpdateProduct = async (
	productId: string,
	body: Omit<ProductUpdateType, 'id'>,
): Promise<ResType | undefined> => {
	const res = await productApi.updateProduct({
		params: { product_id: productId },
		body: body,
	})
	return res
}

// generators
function* registerProduct(
	action: PayloadAction<{
		name: string
		code: string
		usd: number
		index: string
		useYn: boolean
	}>,
) {
	try {
		yield call(apiAddProduct, {
			name: action.payload.name,
			code: action.payload.code,
			usd: action.payload.usd,
			krw: 0,
			discountUsd: 0,
			discountKrw: 0,
			quantity: 0,
			index: Number(action.payload.index),
			useYn: action.payload.useYn ? 1 : 0,
			alwaysAvailableYn: 0,
			soldoutYn: 1,
		})
		const updatedProducts: ResType | undefined = yield call(apiGetProducts)
		yield put(
			productActions.SET_PRODUCT_LIST((updatedProducts?.data || []) as ProductType[]),
		)
		yield put(productActions.REGISTER_PRODUCT_SUCCESS())
	} catch (error: any) {
		yield put(productActions.REGISTER_PRODUCT_FAILURE(error.message))
	}
}

function* deleteProductSaga(action: PayloadAction<string>) {
	try {
		yield call(apiDeleteProduct, action.payload)
		yield put(productActions.DELETE_PRODUCT_SUCCESS(action.payload))
	} catch (error: any) {
		yield put(productActions.DELETE_PRODUCT_FAILURE(error.message))
	}
}

function* updateProductSaga(action: PayloadAction<ProductUpdateType>) {
	try {
		const { id, ...body } = action.payload
		yield call(apiUpdateProduct, id, body)
		yield put(productActions.UPDATE_PRODUCT_SUCCESS())
		const updatedProducts: ResType | undefined = yield call(apiGetProducts)
		yield put(
			productActions.SET_PRODUCT_LIST((updatedProducts?.data || []) as ProductType[]),
		)
	} catch (error: any) {
		yield put(productActions.UPDATE_PRODUCT_FAILURE(error.message))
	}
}

function* triggerInitProductPage() {
	try {
		const apiRes: ResType = yield call(apiGetProducts)

		if (apiRes.status === 200) {
			const tempProductList = apiRes.data[0] !== undefined ? (apiRes.data as any[]) : []
			const productList: ProductType[] = tempProductList.map(
				aProduct => new Product(aProduct as ProductType),
			)

			yield put(productActions.SET_PRODUCT_LIST(productList))
			yield put(productActions.TRIGGER_INIT_PRODUCT_PAGE_SUCCESS())
		}
	} catch (error: any) {
		yield put(productActions.TRIGGER_INIT_PRODUCT_PAGE_FAILURE(error.message))
	}
}

/** 그리드 빈 칸('' · null · undefined)은 NULL 로 — 0 과 '없음' 을 가른다(재료원가) */
const toNullableNumber = (value: unknown): number | null =>
	value === null ||
	value === undefined ||
	String(value).trim() === '' ||
	Number.isNaN(Number(value))
		? null
		: Number(value)

function* updateItem() {
	try {
		const body: ProductStateType['updateItem'] = yield select(
			state => state.product.updateItem,
		)

		switch (body.iudFlag) {
			case 'I':
				yield call(apiAddProduct, {
					code: body.code,
					name: body.name,
					usd: Number(body.usd),
					krw: Number(body.krw),
					discountUsd: Number(body.discountUsd),
					discountKrw: Number(body.discountKrw),
					materialCostUsd: toNullableNumber(body.materialCostUsd),
					materialCostKrw: toNullableNumber(body.materialCostKrw),
					quantity: Number(body.quantity),
					index: body.index,
					useYn: body.useYn,
					alwaysAvailableYn: body.alwaysAvailableYn,
					soldoutYn: body.soldoutYn,
				} as ProductAddType)
				break
			case 'U':
				yield call(apiUpdateProduct, body.id, {
					name: body.name,
					code: body.code,
					usd: Number(body.usd),
					krw: Number(body.krw),
					discountUsd: Number(body.discountUsd),
					discountKrw: Number(body.discountKrw),
					materialCostUsd: toNullableNumber(body.materialCostUsd),
					materialCostKrw: toNullableNumber(body.materialCostKrw),
					quantity: Number(body.quantity),
					index: body.index,
					useYn: body.useYn,
					alwaysAvailableYn: body.alwaysAvailableYn,
					soldoutYn: body.soldoutYn,
				} as ProductUpdateType)
				break
			case 'D':
				yield call(apiDeleteProduct, body.id)
				break
		}

		yield put(productActions.TRIGGER_INIT_PRODUCT_PAGE(true))
		yield put(productActions.TRIGGER_INIT_PRODUCT_PAGE_SUCCESS())
	} catch (error) {
		Logger.warn(error)
	}
}

// watch saga
export function* watchTriggerInitProductPage() {
	yield takeEvery(productActions.TRIGGER_INIT_PRODUCT_PAGE.type, triggerInitProductPage)
}

export function* watchDeleteProduct() {
	yield takeEvery(productActions.DELETE_PRODUCT.type, deleteProductSaga)
}

export function* watchRegisterProduct() {
	yield takeEvery(productActions.REGISTER_PRODUCT.type, registerProduct)
}

export function* watchUpdateProduct() {
	yield takeEvery(productActions.UPDATE_PRODUCT.type, updateProductSaga)
}

function* mainSaga() {
	yield takeEvery(productActions.UPDATE_ITEM, updateItem)
}

// saga
export default function* rootSaga() {
	yield all([
		watchRegisterProduct(),
		watchUpdateProduct(),
		watchDeleteProduct(),
		watchTriggerInitProductPage(),
		fork(mainSaga),
	])
}
